import React, { useEffect, useState } from "react";
import { Gift, Trophy, Mail, MapPin, Store, Loader2, Check, Clock, X as XIcon, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * "My Prizes" — every Daily Stash & Loot Auction win the hunter has ever
 * landed, with their fulfillment status. Hunter submits delivery details
 * here; admin fulfills from the AdminPage Claims queue and writes back the
 * gift card code / tracking # / pickup window which appears here.
 */
export default function MyPrizesPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);

  const reload = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/stash/claims/me");
      setData(data);
    } catch {
      setData({ items: [], counts: {} });
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { reload(); }, []);

  if (busy && !data) {
    return (
      <div className="py-20 text-center" data-testid="my-prizes-loading">
        <Loader2 size={28} className="inline animate-spin text-[#D7A62A]" />
      </div>
    );
  }

  const items = data?.items || [];
  const counts = data?.counts || {};

  return (
    <div className="py-10 space-y-8" data-testid="my-prizes-page">
      <header className="max-w-3xl mx-auto text-center">
        <div className="overline" style={{ color: "#D7A62A" }}>your loot</div>
        <h1 className="font-display uppercase text-5xl md:text-6xl tracking-tight mt-2 leading-[0.9]">
          My <span style={{ color: "#76B82A" }}>Prizes</span>.
        </h1>
        <p className="mt-3 text-base text-[#F5F2E8]/75 max-w-xl mx-auto">
          Every prize you've won from the Daily Stash and Loot Auctions, plus how
          to actually get it in your hands.
        </p>
      </header>

      {/* Status counts */}
      <div className="max-w-3xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3">
        <CountTile label="Unclaimed" value={counts.unclaimed || 0} accent="#D7A62A" hint="needs your details" />
        <CountTile label="Submitted" value={counts.submitted || 0} accent="#F5F2E8" hint="we're on it" />
        <CountTile label="Fulfilled" value={counts.fulfilled || 0} accent="#76B82A" hint="delivered" />
        <CountTile label="Expired" value={counts.expired || 0} accent="#E53935" hint="missed window" />
      </div>

      {items.length === 0 ? (
        <div className="max-w-2xl mx-auto loot-card-flat p-8 text-center" data-testid="my-prizes-empty">
          <Gift size={32} className="mx-auto text-[#D7A62A] mb-2" />
          <div className="font-display uppercase text-xl">No prizes yet</div>
          <div className="font-mono text-xs text-[#F5F2E8]/55 mt-2 max-w-md mx-auto">
            Crack the Daily Stash or place a winning Loot Auction bid — anything
            you win will show up here ready to claim.
          </div>
        </div>
      ) : (
        <div className="max-w-3xl mx-auto space-y-3">
          {items.map((c) => (
            <ClaimRow key={c.id} claim={c} onAct={() => setActive(c)} />
          ))}
        </div>
      )}

      <p className="max-w-2xl mx-auto text-center font-mono text-[10px] text-[#F5F2E8]/45">
        Unclaimed prizes expire 30 days after the win. Don't sleep on yours.
      </p>

      {active && (
        <ClaimModal
          claim={active}
          onClose={() => setActive(null)}
          onSubmitted={() => { setActive(null); reload(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function CountTile({ label, value, accent, hint }) {
  return (
    <div className="loot-card-flat p-3" data-testid={`my-prizes-count-${label.toLowerCase()}`}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55">{label}</div>
      <div className="font-display text-2xl mt-1" style={{ color: accent }}>{value}</div>
      <div className="font-mono text-[9px] text-[#F5F2E8]/45 mt-1">{hint}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    unclaimed: { label: "UNCLAIMED — submit your details", color: "#D7A62A", icon: <AlertTriangle size={12} /> },
    submitted: { label: "SUBMITTED — admin queued", color: "#F5F2E8", icon: <Clock size={12} /> },
    fulfilled: { label: "FULFILLED — see details below", color: "#76B82A", icon: <Check size={12} /> },
    expired:   { label: "EXPIRED",                          color: "#E53935", icon: <XIcon size={12} /> },
  };
  const m = map[status] || map.unclaimed;
  return (
    <div
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-widest uppercase border"
      style={{ color: m.color, borderColor: m.color }}
    >
      {m.icon} {m.label}
    </div>
  );
}

function ClaimRow({ claim, onAct }) {
  const dollars = (claim.value_cents || 0) / 100;
  return (
    <div className="loot-card-flat p-4 space-y-3" data-testid={`claim-row-${claim.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        {claim.prize_image_url && (
          <img src={claim.prize_image_url} alt="" className="h-14 w-14 object-contain border border-[#F5F2E8]/15" />
        )}
        <div className="flex-1 min-w-[200px]">
          <div className="font-display uppercase text-lg leading-tight">{claim.prize_label}</div>
          <div className="font-mono text-[10px] text-[#F5F2E8]/55 mt-1">
            ${dollars.toFixed(2)} · {claim.prize_kind || "prize"} · code <strong>{claim.claim_code}</strong>
          </div>
          <div className="mt-2"><StatusPill status={claim.status} /></div>
        </div>
        <div className="text-right">
          {claim.status === "unclaimed" && (
            <button
              onClick={onAct}
              className="btn-loot text-xs"
              data-testid={`claim-row-submit-${claim.id}`}
            >
              Claim it
            </button>
          )}
        </div>
      </div>

      {claim.status === "fulfilled" && claim.fulfillment_payload && (
        <div className="border-l-2 pl-3 border-[#76B82A] bg-[#76B82A]/5 p-2 text-sm" data-testid={`claim-row-payload-${claim.id}`}>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#76B82A] mb-1">Your prize</div>
          <div className="font-display text-base break-words">{claim.fulfillment_payload}</div>
          {claim.fulfillment_notes && (
            <div className="font-mono text-[10px] text-[#F5F2E8]/55 mt-1">{claim.fulfillment_notes}</div>
          )}
        </div>
      )}

      {claim.status === "submitted" && (
        <div className="font-mono text-[11px] text-[#F5F2E8]/55 leading-snug">
          Sit tight — your details landed in the queue. We'll send your gift / tracking # to {claim.contact_email || "you"} soon.
        </div>
      )}
    </div>
  );
}

function ClaimModal({ claim, onClose, onSubmitted }) {
  const [method, setMethod] = useState("digital");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (method === "digital" && !email.trim()) return toast.error("Email required for digital prizes.");
    if (method === "mail" && !address.trim()) return toast.error("Mailing address required.");
    setBusy(true);
    try {
      await api.post(`/stash/claims/${claim.id}/submit`, {
        fulfillment_method: method,
        contact_email: email.trim() || null,
        contact_phone: phone.trim() || null,
        shipping_address: address.trim() || null,
        notes: notes.trim() || null,
      });
      toast.success("Claim submitted! We'll be in touch.");
      onSubmitted();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't submit");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-end md:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="claim-modal"
    >
      <div
        className="loot-card-flat max-w-lg w-full p-5 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="overline" style={{ color: "#D7A62A" }}>claim your prize</div>
            <div className="font-display uppercase text-xl mt-1">{claim.prize_label}</div>
            <div className="font-mono text-[10px] text-[#F5F2E8]/55 mt-1">
              code <strong>{claim.claim_code}</strong> · ${(claim.value_cents / 100).toFixed(2)}
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost text-xs">Close</button>
        </div>

        <div className="space-y-2">
          <div className="overline mb-1">How would you like to receive it?</div>
          <div className="grid grid-cols-3 gap-2">
            <MethodTile active={method === "digital"} onClick={() => setMethod("digital")} icon={<Mail size={16} />} label="Digital code" hint="Email me the code" testid="claim-method-digital" />
            <MethodTile active={method === "mail"}    onClick={() => setMethod("mail")}    icon={<MapPin size={16} />} label="Mail it"     hint="Ship to my address" testid="claim-method-mail" />
            <MethodTile active={method === "pickup"}  onClick={() => setMethod("pickup")}  icon={<Store size={16} />} label="Pickup"      hint="At sponsor location" testid="claim-method-pickup" />
          </div>
        </div>

        <div className="space-y-2">
          {(method === "digital" || method === "pickup") && (
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Email</label>
              <input
                className="loot-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@somewhere.com"
                data-testid="claim-email"
              />
            </div>
          )}
          {method === "mail" && (
            <>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Mailing address</label>
                <textarea
                  className="loot-input"
                  rows={3}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="123 Main St, Anytown, USA 12345"
                  maxLength={600}
                  data-testid="claim-address"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Phone (for delivery questions, optional)</label>
                <input className="loot-input" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} data-testid="claim-phone" />
              </div>
            </>
          )}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Notes (optional)</label>
            <textarea
              className="loot-input"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Best time to swing by, t-shirt size, anything we should know"
              maxLength={400}
              data-testid="claim-notes"
            />
          </div>
        </div>

        <button
          onClick={submit}
          disabled={busy}
          className="btn-loot w-full"
          data-testid="claim-submit"
        >
          {busy ? <Loader2 size={14} className="inline animate-spin mr-1" /> : <Trophy size={14} className="inline mr-1" />}
          Submit claim
        </button>

        <p className="font-mono text-[10px] text-[#F5F2E8]/45 text-center">
          Once submitted, we'll fulfill within ~3 business days. You'll see the
          gift card code / tracking # right back on this page.
        </p>
      </div>
    </div>
  );
}

function MethodTile({ active, onClick, icon, label, hint, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="loot-card-flat p-3 text-left hover:border-[#76B82A] transition-colors"
      style={{ borderColor: active ? "#76B82A" : undefined, background: active ? "rgba(118,184,42,0.08)" : undefined }}
    >
      <div className="flex items-center gap-1.5" style={{ color: active ? "#76B82A" : "#F5F2E8" }}>
        {icon}
        <span className="font-display uppercase text-xs">{label}</span>
      </div>
      <div className="font-mono text-[9px] text-[#F5F2E8]/55 mt-1 leading-snug">{hint}</div>
    </button>
  );
}
